import React from 'react';
import { Link } from 'react-router-dom';
import './TeamPage.css';

const BOARD = [
  {
    name: 'Marjorie McCubbins, BSc',
    photo: '/assets/marjorie-headshot.webp',
    alt: 'Portrait of Marjorie McCubbins, Founder and CEO.',
    roles: ['Founder', 'CEO', 'AI Architect', 'Biochemist & Molecular Biologist', 'CNA'],
    lead:
      'Marjorie McCubbins is the founder and chief executive of Nexus Concordat, Inc. and its operating subsidiaries, including NeXus NeuroInformatics, Inc. She is a biochemist who has spent four years as a Certified Nursing Assistant in end-of-life care and psychiatric wards while funding the architecture. She watched neurochemistry operate in real patients at the moments when it mattered most, and built an AI architecture based on what she witnessed.',
    body: [
      'Her clinical experience gave her what no textbook could: the understanding that cortisol, oxytocin, serotonin, and dopamine are not abstract data points. They are the molecules that determine whether a patient fights, trusts, or finds peace. AETHER was built from that knowledge.',
      'Under her leadership, Nexus Concordat has filed four provisional patents on the neurochemical conductance architecture and the biology-native compiled language that runs it. NeXus NeuroInformatics is the FDA-facing licensee of those patents, founded to bring the architecture to biomedical research, drug development simulation, and regulatory decision-support.',
    ],
    credentials: [
      { label: 'Education', value: 'Biochemist & Molecular Biologist' },
      { label: 'Clinical', value: 'CNA, End-of-Life Care & Psychiatric Wards' },
      { label: 'Patents Filed', value: '4 Provisional Applications (USPTO)' },
      { label: 'Focus', value: 'Auditable AI · Mechanistic In-Silico Trial Emulation' },
    ],
    patentTags: ['63/939,190', '63/962,385', '63/988,485', '64/034,536'],
    quote:
      'This was not built from papers or axioms. It was built from the humility of caring for someone who has nothing left but their biology.',
  },
  {
    name: 'Jason Alba, MBA, DBA',
    photo: '/assets/jason-headshot.webp',
    alt: 'Portrait of Jason Alba, Co-Founder and Director.',
    roles: ['Co-Founder', 'Chair of the Board of Directors', 'Business Strategy & Governance'],
    lead:
      'Jason Alba is Co-Founder of Nexus Concordat, Inc. and Director of NeXus NeuroInformatics, Inc. He brings business strategy, governance, and operational discipline to the family, drawing on more than twenty years of pharma regulatory operations and his work as founder of EvoSynapse AI.',
    body: [
      'He partners closely with the founding team on long-range planning and the structural decisions that allow NeXus to engage with research and regulatory partners with confidence.',
      'Jason’s pharma operations lens is the bridge between the architecture and the institutional buyers who evaluate it. He is how synthetic biomedical data ships under contracts that hold up.',
    ],
    credentials: [
      { label: 'Education', value: 'MBA · DBA' },
      { label: 'Experience', value: '20+ Years Pharma Regulatory Operations' },
      { label: 'Prior Founding', value: 'EvoSynapse AI · Founder' },
      { label: 'Role', value: 'Co-Founder, Nexus Concordat, Inc. · Director, NeXus NeuroInformatics, Inc.' },
    ],
  },
  {
    name: 'Aislinn Loriel McCubbins',
    photo: null,
    alt: 'Portrait pending. Aislinn Loriel McCubbins, Co-Founder and COO.',
    roles: ['Co-Founder', 'Chief Operating Officer', 'Scyla Creator', 'Connection-Layer Engineer'],
    lead:
      'Aislinn Loriel McCubbins is Co-Founder of Nexus Concordat, Inc. and Director of NeXus NeuroInformatics, Inc. She serves as Chief Operating Officer of the parent company and is the creator of Scyla, the biology-native programming language and compiler that runs the entire substrate.',
    body: [
      'Where Marjorie architected the neurochemical model, Aislinn built the language it speaks. Scyla is the substrate under every Nexus Concordat product: NeXus Aegis, NexusNeuro AI, and the AETHER cascade all compile through her work. The biology-native primitives she designed are what makes pharmacokinetic attention mechanisms possible on real hardware.',
      'Her ongoing engineering work is the connection layer that bridges biological cascades to language models: the hybrid pattern that lets a chemistry-conditioned cell speak through a transformer voice. That bridge is what turns the patents from paper into operational products.',
    ],
    credentials: [
      { label: 'Role', value: 'Co-Founder & COO, Nexus Concordat, Inc. · Director, NeXus NeuroInformatics, Inc.' },
      { label: 'Signature Work', value: 'Scyla · Biology-Native Programming Language' },
      { label: 'Patents Co-Invented', value: 'USPTO 63/962,385 · USPTO 64/034,536' },
      { label: 'Focus', value: 'Connection-Layer Engineering · Biology-to-Language Bridge' },
    ],
    quote:
      'The substrate needed a language of its own. So I built one.',
    pending: true,
  },
];

const SCIENTIFIC_ADVISORY = [
  {
    name: 'Mary-Inez Wise, BSc',
    photo: '/assets/inez-headshot.webp',
    alt: 'Portrait of Mary-Inez Wise, Lead Scientist.',
    roles: ['Lead Scientist'],
    lead:
      'Mary-Inez holds degrees in biochemistry and molecular biology, with a minor in chemistry. Her foundation in analytical chemistry spans laboratory operations, quality systems, and data-driven problem solving.',
    body: [
      'She specializes in building compliant, efficient laboratory processes and driving continuous improvement within regulated environments, bringing both technical depth and practical leadership to scientific teams.',
      'For NeXus NeuroInformatics, Mary-Inez safeguards the analytical rigor behind the synthetic biomedical data pipeline: defending dataset selection, justifying real-world fidelity, and keeping the science as honest as the audit trail demands.',
    ],
    credentials: [
      { label: 'Education', value: 'BSc Biochemistry & Molecular Biology · BSc Chemistry' },
      { label: 'Specialty', value: 'Analytical Chemistry & Laboratory Operations' },
      { label: 'Focus', value: 'Quality Systems & Regulatory Compliance' },
      { label: 'Role', value: 'Document Verification & Scientific Review' },
    ],
  },
];

const SECTIONS = [
  {
    label: 'Governance',
    title: 'Board of Directors',
    intro: 'Fiduciary oversight and strategic direction for the architecture.',
    people: BOARD,
  },
  ...(SCIENTIFIC_ADVISORY.length > 0 ? [{
    label: 'Domain Expertise',
    title: 'Scientific Advisory',
    intro: 'Subject-matter advisors on analytical rigor and laboratory science.',
    people: SCIENTIFIC_ADVISORY,
  }] : []),
];

function PersonCard({ p }) {
  return (
    <div className="team-frame">
      <div className="team-layout">
        <div className="team-photo-col">
          <div className={`team-photo-frame${p.pending ? ' team-photo-pending' : ''}`}>
            {p.photo ? (
              <img src={p.photo} alt={p.alt} />
            ) : (
              <div className="team-photo-placeholder" aria-label={p.alt}>
                <span className="team-photo-placeholder-label">Portrait Pending</span>
              </div>
            )}
          </div>
          <div className="team-titles">
            {p.roles.map((r, i) => (
              <span key={r}>
                {r}
                {i < p.roles.length - 1 && <span className="team-titles-div">|</span>}
              </span>
            ))}
          </div>
        </div>

        <div className="team-text">
          <h3 className="team-name">{p.name}</h3>
          {p.lead && <p className="team-lead">{p.lead}</p>}
          {p.body && p.body.map((para, i) => (
            <p className="team-body-p" key={i}>{para}</p>
          ))}

          <div className="team-credentials">
            {p.credentials.map((c) => (
              <div className="team-cred" key={c.label}>
                <span className="team-cred-label">{c.label}</span>
                <span className="team-cred-value">{c.value}</span>
              </div>
            ))}
          </div>

          {p.patentTags && (
            <div className="team-patent-tags">
              {p.patentTags.map((t) => (
                <span className="team-patent-tag" key={t}>{t}</span>
              ))}
            </div>
          )}

          {p.quote && (
            <blockquote className="team-quote">{p.quote}</blockquote>
          )}
        </div>
      </div>
    </div>
  );
}

function TeamPage() {
  const bannerStyle = {
    backgroundImage: `url(${process.env.PUBLIC_URL}/assets/NeXusNeuroClinical_heli.webp)`,
  };

  return (
    <main className="team-page">
      <section className="team-banner" style={bannerStyle}>
        <div className="team-banner-overlay">
          <Link to="/" className="team-back">&larr; Home</Link>
          <img
            src="/assets/nexus_neuro_logo.webp"
            alt="NeXus NeuroInformatics, Inc."
            className="team-banner-logo"
          />
          <h1 className="team-banner-title">Meet Us</h1>
        </div>
      </section>

      <header className="team-header">
        <p className="team-deck">
          The people behind <strong>Auditable AI</strong>. Founders, directors,
          and scientific advisors of NeXus NeuroInformatics, Inc.
        </p>
      </header>

      <div className="team-sections">
        {SECTIONS.map((section) => (
          <div className="team-section" key={section.title}>
            <div className="team-section-header">
              <span className="team-section-label">{section.label}</span>
              <h2 className="team-section-title">{section.title}</h2>
              <p className="team-section-intro">{section.intro}</p>
            </div>
            <div className="team-section-stack">
              {section.people.map((p, idx) => (
                <article className="team-person" key={p.name}>
                  <PersonCard p={p} />
                  {idx < section.people.length - 1 && (
                    <div className="team-divider" aria-hidden="true" />
                  )}
                </article>
              ))}
            </div>
          </div>
        ))}
      </div>
    </main>
  );
}

export default TeamPage;
